import { DiscoveryApi, FetchApi } from '@backstage/core-plugin-api';
import {
  GeminiAgentApi,
  GeminiAgentSession,
  CreateSessionRequest,
  CreateSessionOptions,
  ListSessionsOptions,
  SessionListResult,
  CredentialsCheckResult,
  CheckCredentialsOptions,
  LogEntry,
  ChatHistory,
  UpdateSessionRequest,
  ListPromptsOptions,
  PromptListResult,
  StoredPrompt,
  CreatePromptRequest,
  UpdatePromptRequest,
  HealthStatus,
} from './GeminiAgentApi';

export class GeminiAgentClient implements GeminiAgentApi {
  private readonly discoveryApi: DiscoveryApi;
  private readonly fetchApi: FetchApi;

  constructor(discoveryApi: DiscoveryApi, fetchApi: FetchApi) {
    this.discoveryApi = discoveryApi;
    this.fetchApi = fetchApi;
  }

  private async getBaseUrl(): Promise<string> {
    return this.discoveryApi.getBaseUrl('gemini-agent');
  }

  private async fetch<T>(
    path: string,
    options: RequestInit = {},
  ): Promise<T> {
    const baseUrl = await this.getBaseUrl();
    const url = `${baseUrl}${path}`;

    const response = await this.fetchApi.fetch(url, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        ...options.headers,
      },
    });

    if (!response.ok) {
      let errorMessage = `Gemini Agent API error (${response.status})`;
      const text = await response.text();
      try {
        const body = JSON.parse(text);
        // Preserve the structured detail from the backend
        if (body.detail) {
          errorMessage = `${body.error || 'Error'}: ${body.detail}`;
        } else if (body.error) {
          errorMessage = body.error;
        }
      } catch {
        if (text) errorMessage = `${errorMessage} - ${text}`;
      }
      throw new Error(errorMessage);
    }

    if (
      response.status === 204 ||
      response.headers.get('content-length') === '0'
    ) {
      return undefined as T;
    }

    const contentType = response.headers.get('content-type');
    if (contentType && contentType.includes('application/json')) {
      return response.json();
    }

    return response.text() as T;
  }

  async getHealth(): Promise<HealthStatus> {
    return this.fetch<HealthStatus>('/health');
  }

  async listSessions(options?: ListSessionsOptions): Promise<SessionListResult> {
    const searchParams = new URLSearchParams();
    if (options?.includeInactive) searchParams.set('includeInactive', 'true');
    if (options?.mine) searchParams.set('mine', 'true');
    const qs = searchParams.toString();
    const params = qs ? `?${qs}` : '';
    const data = await this.fetch<any>(`/sessions${params}`);

    // Handle both new format { sessions: [], warning?: {} } and legacy bare array
    if (Array.isArray(data)) {
      return { sessions: data };
    }

    return {
      sessions: data.sessions ?? [],
      currentUserId: data.currentUserId,
      warning: data.warning,
    };
  }

  async createSession(
    request: CreateSessionRequest,
    options?: CreateSessionOptions,
  ): Promise<GeminiAgentSession> {
    const headers: Record<string, string> = {};
    if (options?.githubToken) {
      headers['X-GitHub-Token'] = options.githubToken;
    }
    if (options?.googleAccessToken) {
      headers['X-Gcp-Access-Token'] = options.googleAccessToken;
    }

    return this.fetch<GeminiAgentSession>('/sessions', {
      method: 'POST',
      headers,
      body: JSON.stringify(request),
    });
  }

  async getSession(sessionId: string): Promise<GeminiAgentSession> {
    return this.fetch<GeminiAgentSession>(`/sessions/${encodeURIComponent(sessionId)}`);
  }

  async deleteSession(sessionId: string): Promise<void> {
    await this.fetch<void>(`/sessions/${encodeURIComponent(sessionId)}`, {
      method: 'DELETE',
    });
  }

  async enqueueMessage(sessionId: string, text: string): Promise<void> {
    await this.fetch<{ queued: boolean }>(
      `/sessions/${encodeURIComponent(sessionId)}/message`,
      { method: 'POST', body: JSON.stringify({ text }) },
    );
  }

  async fetchLogs(sessionId: string, limit = 200): Promise<LogEntry[]> {
    return this.fetch<LogEntry[]>(
      `/sessions/${encodeURIComponent(sessionId)}/logs?limit=${limit}`,
    );
  }

  async checkCredentials(
    options?: CheckCredentialsOptions,
  ): Promise<CredentialsCheckResult> {
    const headers: Record<string, string> = {};
    if (options?.githubToken) {
      headers['X-GitHub-Token'] = options.githubToken;
    }
    if (options?.googleAccessToken) {
      headers['X-Gcp-Access-Token'] = options.googleAccessToken;
    }

    return this.fetch<CredentialsCheckResult>('/sessions/credentials', {
      headers,
    });
  }

  async getChatHistory(sessionId: string): Promise<ChatHistory> {
    return this.fetch<ChatHistory>(
      `/sessions/${encodeURIComponent(sessionId)}/chat-history`,
    );
  }

  async updateSession(sessionId: string, request: UpdateSessionRequest): Promise<void> {
    await this.fetch<void>(`/sessions/${encodeURIComponent(sessionId)}`, {
      method: 'PATCH',
      body: JSON.stringify(request),
    });
  }

  async cancelTask(sessionId: string): Promise<void> {
    await this.fetch<{ cancelled: boolean }>(
      `/sessions/${encodeURIComponent(sessionId)}/cancel`,
      { method: 'POST' },
    );
  }

  // ── Prompt Library ──────────────────────────────────────────────────

  async listPrompts(options?: ListPromptsOptions): Promise<PromptListResult> {
    const params = new URLSearchParams();
    if (options?.labels?.length) params.set('labels', options.labels.join(','));
    if (options?.owner) params.set('owner', options.owner);
    if (options?.search) params.set('search', options.search);
    const qs = params.toString();
    return this.fetch<PromptListResult>(`/prompts${qs ? `?${qs}` : ''}`);
  }

  async getPrompt(promptId: string): Promise<StoredPrompt> {
    return this.fetch<StoredPrompt>(`/prompts/${encodeURIComponent(promptId)}`);
  }

  async createPrompt(request: CreatePromptRequest): Promise<StoredPrompt> {
    return this.fetch<StoredPrompt>('/prompts', {
      method: 'POST',
      body: JSON.stringify(request),
    });
  }

  async updatePrompt(promptId: string, request: UpdatePromptRequest): Promise<StoredPrompt> {
    return this.fetch<StoredPrompt>(`/prompts/${encodeURIComponent(promptId)}`, {
      method: 'PATCH',
      body: JSON.stringify(request),
    });
  }

  async deletePrompt(promptId: string): Promise<void> {
    await this.fetch<void>(`/prompts/${encodeURIComponent(promptId)}`, {
      method: 'DELETE',
    });
  }
}
